import { gql, useQuery } from '@apollo/client';
import { getProductPin } from '../../context/ProductContext';

/**
 * The tinycms plans (Free, Personal, Business, Pro) as signup and Plan & billing
 * show them. Prices and features come from the server (Query.tinycmsPlans,
 * apiserver platform/services/plans.js) so the screens never disagree with
 * what an account is actually charged.
 */

export const TINYCMS_PLANS_QUERY = gql`
  query TinycmsPlans {
    tinycmsPlans {
      key
      label
      summary
      monthlyPrice
      yearlyPrice
      trialPeriodDays
      products
      features
      maxDomains
      maxMembers
    }
  }
`;

export type BillingInterval = 'month' | 'year';

export interface TinycmsPlan {
  key: string;
  label: string;
  summary: string;
  monthlyPrice: number;
  yearlyPrice: number;
  trialPeriodDays: number;
  products: string[];
  features: string[];
  maxDomains: number;
  maxMembers: number;
}

export function useTinycmsPlans() {
  const { data, loading, error } = useQuery<{ tinycmsPlans: TinycmsPlan[] }>(TINYCMS_PLANS_QUERY);
  return { plans: data?.tinycmsPlans || [], loading, error };
}

/** True on a tinycms host or build (app.tinycms.ai), where signup sells these plans. */
export const isTinycmsFunnel = () => getProductPin() === 'atomizer';

export const toInterval = (v: string | null | undefined): BillingInterval =>
  v === 'month' ? 'month' : 'year';

/** Per-month price for the interval. */
export const planPrice = (plan: TinycmsPlan, interval: BillingInterval) =>
  interval === 'year' ? plan.yearlyPrice : plan.monthlyPrice;

export const money = (n: number) => (Number.isInteger(n) ? `$${n}` : `$${n.toFixed(2)}`);

/** "Free" · "$24/mo, billed monthly" · "$18/mo, billed yearly ($216)". */
export function billingLine(plan: TinycmsPlan, interval: BillingInterval) {
  const price = planPrice(plan, interval);
  if (!price) return 'Free';
  return interval === 'year'
    ? `${money(price)}/mo, billed yearly (${money(price * 12)})`
    : `${money(price)}/mo, billed monthly`;
}

/** What is charged when the trial ends: "$216 a year" or "$24 a month". */
export function chargeLine(plan: TinycmsPlan, interval: BillingInterval) {
  const price = planPrice(plan, interval);
  return interval === 'year' ? `${money(price * 12)} a year` : `${money(price)} a month`;
}
